<template>
  <div class="card card-custom card-stretch gutter-b theme-module">
    <div class="card-body pt-8">
      <div class="settings-page-head mb-6 px-2">
        <h3 class="card-label text-primary font-weight-bolder mb-1">Website Themes</h3>
        <small class="text-muted">
          Edit colours, banners and content below. To switch layout, click <strong>Change theme</strong>. Your previous theme is kept if you switch back.
        </small>
      </div>

      <div class="theme-picker">
        <div v-if="loading" class="theme-selected-bar">
          <div class="theme-selected-thumb skel-shimmer"></div>
          <div class="theme-selected-copy">
            <div class="theme-skel-label skel-shimmer" style="margin: 0; width: 140px;"></div>
            <div class="theme-skel-label skel-shimmer" style="margin: 8px 0 0; width: 90px; height: 10px;"></div>
          </div>
        </div>

        <div v-else-if="selectedTheme" class="theme-selected-bar">
          <button
            v-if="selectedTheme.image"
            type="button"
            class="theme-selected-thumb"
            :title="'Preview ' + selectedTheme.label"
            @click="openPreview(selectedTheme)"
          >
            <img
              :src="selectedTheme.image"
              :alt="selectedTheme.label"
            />
          </button>
          <div v-else class="theme-selected-thumb">
            {{ themeInitials(selectedTheme.label) }}
          </div>
          <div class="theme-selected-copy">
            <div class="theme-selected-name">
              {{ selectedTheme.label }}
              <span v-if="isSelectedLive" class="theme-status theme-status-live">Live</span>
              <span v-else class="theme-status theme-status-draft">Not published</span>
            </div>
            <div class="theme-picker-meta mb-0">
              {{ isSelectedLive ? "This layout is live on your website." : "Selected for editing — click Use this theme to publish." }}
            </div>
          </div>
          <div class="theme-selected-actions">
            <button
              v-if="liveWebsiteUrl"
              type="button"
              class="btn btn-outline-primary btn-sm"
              @click="openLiveWebsite"
            >
              View live website
            </button>
            <button
              type="button"
              class="btn btn-sm theme-change-btn"
              @click="openThemeModal"
            >
              Change theme
            </button>
            <button
              v-if="$hasPermission('manage business settings') || $hasPermission('manage business settings ')"
              type="button"
              class="btn btn-primary btn-sm"
              :disabled="isSelectedLive || publishing"
              @click="useSelectedTheme"
            >
              {{ publishing ? "Publishing…" : (isSelectedLive ? "This theme is live" : "Use this theme") }}
            </button>
          </div>
        </div>
      </div>

      <TemplateSettings
        v-if="theme && !loading"
        ref="templateSettings"
        :key="'tpl-' + theme"
        :template="theme"
      />

      <b-modal
        id="theme-picker-modal"
        v-model="showThemeGrid"
        title="Choose a theme"
        size="xl"
        centered
        scrollable
        modal-class="theme-picker-modal"
        body-class="theme-picker-modal-body"
        footer-class="theme-picker-modal-footer"
      >
        <div class="theme-picker-toolbar">
          <div>
            <div class="theme-picker-label">Select a layout</div>
            <div v-if="pendingTheme" class="theme-picker-meta">
              {{ pendingTheme.label }} selected
              <span v-if="isPendingLive" class="theme-status theme-status-live">Live</span>
              <span v-else class="theme-status theme-status-draft">Click Apply this theme to publish</span>
            </div>
          </div>
          <input
            type="search"
            class="form-control theme-search"
            v-model="themeQuery"
            placeholder="Search themes"
          />
        </div>

        <div class="theme-filter-row">
          <button
            type="button"
            class="theme-filter"
            :class="{ 'theme-filter-active': themeFilter === 'all' }"
            @click="themeFilter = 'all'"
          >
            All themes
          </button>
          <button
            type="button"
            class="theme-filter"
            :class="{ 'theme-filter-active': themeFilter === 'current' }"
            @click="themeFilter = 'current'"
          >
            Current live theme
          </button>
        </div>

        <div v-if="filteredThemes.length" class="theme-grid-wrap">
          <div class="theme-grid">
            <div
              v-for="item in filteredThemes"
              :key="item.value"
              class="theme-card"
              :class="{
                'theme-card-selected': item.value === pendingThemeId,
                'theme-card-live': item.value === currentThemeId
              }"
            >
              <div class="theme-phone" @click="selectTheme(item)">
                <img
                  v-if="item.image"
                  :src="item.image"
                  class="theme-card-image"
                  :alt="item.label"
                />
                <div v-else class="theme-card-fallback">{{ themeInitials(item.label) }}</div>
                <div class="theme-phone-overlay">
                  <button
                    v-if="item.image"
                    type="button"
                    class="theme-overlay-btn"
                    @click.stop="openPreview(item)"
                  >
                    Preview
                  </button>
                  <button
                    type="button"
                    class="theme-overlay-btn theme-overlay-btn-primary"
                    @click.stop="selectTheme(item)"
                  >
                    {{ item.value === pendingThemeId ? "Selected" : "Choose" }}
                  </button>
                </div>
                <span v-if="item.value === currentThemeId" class="theme-card-badge">Live</span>
                <span v-else-if="item.value === pendingThemeId" class="theme-card-badge theme-card-badge-selected">Selected</span>
              </div>
              <div class="theme-card-name">{{ item.label }}</div>
            </div>
          </div>
        </div>

        <div v-else class="theme-empty">
          No themes match “{{ themeQuery }}”.
        </div>

        <template #modal-footer>
          <div class="theme-modal-footer-copy">
            <template v-if="pendingTheme">
              <strong>{{ pendingTheme.label }}</strong>
              <span v-if="isPendingLive"> is already live.</span>
              <span v-else> is selected. Apply it to publish this layout to your website.</span>
            </template>
            <template v-else>Choose a theme, then apply it to your website.</template>
          </div>
          <button type="button" class="btn btn-light" @click="showThemeGrid = false">
            Close
          </button>
          <button
            v-if="($hasPermission('manage business settings') || $hasPermission('manage business settings ')) && !isPendingLive"
            type="button"
            class="btn btn-primary"
            :disabled="!pendingTheme || publishing"
            @click="applyPendingTheme"
          >
            {{ publishing ? "Applying…" : "Apply this theme" }}
          </button>
        </template>
      </b-modal>
    </div>

    <div
      v-if="previewTheme"
      class="theme-lightbox"
      @click.self="previewTheme = null"
    >
      <div class="theme-lightbox-card">
        <div class="theme-lightbox-head">
          <div>
            <div class="theme-lightbox-title">{{ previewTheme.label }}</div>
            <div class="theme-lightbox-code">{{ previewTheme.text }}</div>
          </div>
          <button type="button" class="theme-lightbox-close" @click="previewTheme = null">×</button>
        </div>
        <img
          v-if="previewTheme.image"
          :src="previewTheme.image"
          class="theme-lightbox-image"
          :alt="previewTheme.label"
        />
        <p class="theme-lightbox-note">
          This is a layout preview. Your live banners, colours and text are not shown here.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { GET_TEMPLATES } from "@/core/services/store/profile.module";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import TemplateSettings from "@/view/pages/business/components/settings/TemplateSettings";
import { mapGetters } from "vuex";
import Swal from "sweetalert2";

export default {
  name: "Setting",
  components: {
    TemplateSettings
  },
  computed: {
    ...mapGetters(["currentUser", "currentUserPersonalInfo"]),
    currentThemeId() {
      return parseInt(this.currentUserPersonalInfo.template_id, 10) || null;
    },
    selectedTheme() {
      return this.themes.find((item) => item.value === this.themeId) || null;
    },
    isSelectedLive() {
      return this.themeId != null && this.themeId === this.currentThemeId;
    },
    pendingTheme() {
      return this.themes.find((item) => item.value === this.pendingThemeId) || null;
    },
    isPendingLive() {
      return this.pendingThemeId != null && this.pendingThemeId === this.currentThemeId;
    },
    liveWebsiteUrl() {
      return (this.currentUserPersonalInfo && this.currentUserPersonalInfo.store_url) || "";
    },
    filteredThemes() {
      const query = (this.themeQuery || "").trim().toLowerCase();
      let list = this.themes.filter((item) => item.text);

      if (this.themeFilter === "current" && this.currentThemeId) {
        list = list.filter((item) => item.value === this.currentThemeId);
      }

      const matched = query
        ? list.filter((item) => {
            const haystack = [item.label, item.text, String(item.value)]
              .join(" ")
              .toLowerCase();
            return haystack.indexOf(query) !== -1;
          })
        : list;

      return matched.slice().sort((a, b) => {
        if (a.value === this.currentThemeId) return -1;
        if (b.value === this.currentThemeId) return 1;
        const aNum = this.themeNumber(a);
        const bNum = this.themeNumber(b);
        if (aNum !== bNum) return aNum - bNum;
        return String(a.text).localeCompare(String(b.text));
      });
    }
  },
  data() {
    return {
      loading: true,
      publishing: false,
      showThemeGrid: false,
      themeQuery: "",
      themeFilter: "all",
      themes: [],
      theme: null,
      themeId: null,
      pendingThemeId: null,
      previewTheme: null
    };
  },
  methods: {
    themeLabel(name) {
      const raw = String(name || "").trim();
      const match = raw.match(/(\d+)/);
      if (match) {
        return "Theme " + match[1];
      }
      return raw || "Theme";
    },
    themeNumber(item) {
      const fromName = String((item && item.text) || "").match(/(\d+)/);
      if (fromName) {
        return parseInt(fromName[1], 10);
      }
      return (item && item.value) || 0;
    },
    openThemeModal() {
      this.pendingThemeId = this.themeId;
      this.themeQuery = "";
      this.themeFilter = "all";
      this.showThemeGrid = true;
    },
    selectTheme(item) {
      this.pendingThemeId = parseInt(item.value, 10);
    },
    waitForTemplateEditor() {
      return new Promise((resolve, reject) => {
        let tries = 0;
        const tick = () => {
          const editor = this.$refs.templateSettings;
          if (editor && editor.templateData) {
            resolve(editor);
            return;
          }
          tries += 1;
          if (tries > 40) {
            reject(new Error("Theme editor did not load"));
            return;
          }
          setTimeout(tick, 150);
        };
        this.$nextTick(tick);
      });
    },
    async applyPendingTheme() {
      if (!this.pendingTheme || this.isPendingLive || this.publishing) {
        return;
      }

      const result = await Swal.fire({
        title: "Apply this theme?",
        html:
          "Your website will switch to <strong>" +
          this.pendingTheme.label +
          "</strong>. Content already saved for your current theme is kept, so you can switch back later.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, apply this theme",
        cancelButtonText: "Cancel"
      });

      if (!result.value) {
        return;
      }

      const nextId = this.pendingThemeId;
      this.themeId = nextId;
      this.theme = nextId;
      this.showThemeGrid = false;

      this.publishing = true;
      const stopPublishing = () => {
        this.publishing = false;
      };
      const hangGuard = setTimeout(stopPublishing, 12000);
      try {
        const editor = await this.waitForTemplateEditor();
        await editor.saveTemplate();
      } catch (error) {
        console.error(error);
        Swal.fire(
          "Please wait",
          "This theme is still loading. Open Change theme and try Apply again in a moment.",
          "info"
        );
      } finally {
        clearTimeout(hangGuard);
        stopPublishing();
      }
    },
    openPreview(item) {
      this.previewTheme = item;
    },
    openLiveWebsite() {
      if (this.liveWebsiteUrl) {
        window.open(this.liveWebsiteUrl, "_blank");
      }
    },
    themeInitials(name) {
      const parts = String(name || "")
        .trim()
        .split(/\s+/)
        .slice(0, 2);
      if (!parts.length) return "TH";
      return parts.map((part) => part.charAt(0).toUpperCase()).join("");
    },
    async useSelectedTheme() {
      if (!this.selectedTheme || this.isSelectedLive || this.publishing) {
        return;
      }

      this.showThemeGrid = false;

      const result = await Swal.fire({
        title: "Make this theme live?",
        html:
          "Your website will switch to <strong>" +
          this.selectedTheme.label +
          "</strong>. Content already saved for your current theme is kept, so you can switch back later.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, use this theme",
        cancelButtonText: "Cancel"
      });

      if (!result.value) {
        return;
      }

      const editor = this.$refs.templateSettings;
      if (!editor || !editor.templateData) {
        Swal.fire(
          "Please wait",
          "This theme is still loading. Try again in a moment.",
          "info"
        );
        return;
      }

      this.publishing = true;
      const stopPublishing = () => {
        this.publishing = false;
      };
      const hangGuard = setTimeout(stopPublishing, 8000);
      try {
        await editor.saveTemplate();
      } catch (error) {
        console.error(error);
      } finally {
        clearTimeout(hangGuard);
        stopPublishing();
      }
    }
  },
  mounted() {
    const savedId = parseInt(
      this.currentUserPersonalInfo && this.currentUserPersonalInfo.template_id,
      10
    );
    this.theme = Number.isFinite(savedId) ? savedId : null;
    this.themeId = this.theme;
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Business Settings" },
      { title: "Themes" }
    ]);

    this.loading = true;
    this.$store.dispatch(GET_TEMPLATES)
      .then((result) => {
        const list = Array.isArray(result)
          ? result
          : (Array.isArray(result && result.data) ? result.data : []);
        this.themes = list.map((item) => ({
          value: parseInt(item.id, 10),
          text: item.template_name,
          label: this.themeLabel(item.template_name),
          image: item.image
        }));
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.loading = false;
      });
  },
  watch: {
    currentThemeId(id) {
      if (id && !this.themeId) {
        this.theme = id;
        this.themeId = id;
      }
    }
  }
};
</script>

<style lang="scss">
.theme-module {
  margin-top: 8px;
  margin-bottom: 4.5rem;
}

.theme-picker {
  margin-bottom: 28px;
  padding: 18px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}

.theme-picker-list {
  margin-top: 8px;
}

.theme-picker-modal .modal-dialog {
  max-width: 1100px;
}

.theme-picker-modal-body {
  background: #f8fafc;
}

.theme-picker-modal .theme-grid-wrap {
  max-height: min(52vh, 540px);
}

.theme-picker-modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
}

.theme-modal-footer-copy {
  margin-right: auto;
  font-size: 13px;
  color: #475569;
  text-align: left;
  padding-right: 12px;
}

.theme-picker-toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.theme-picker-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.theme-selected-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  padding: 4px 0;
}

.theme-selected-thumb {
  width: 62px;
  height: 118px;
  padding: 0;
  border-radius: 14px;
  overflow: hidden;
  background: #111827;
  border: 2px solid #e5e7eb;
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.14);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 16px;
  font-weight: 700;
  color: #241b5a;
}

button.theme-selected-thumb {
  cursor: pointer;
}

button.theme-selected-thumb:hover {
  border-color: #241b5a;
  transform: translateY(-1px);
}

.theme-selected-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
  display: block;
}

.theme-selected-copy {
  flex: 1 1 180px;
  min-width: 0;
}

.theme-selected-name {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.theme-selected-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.theme-change-btn {
  font-weight: 700;
  color: #241b5a;
  background: #eef2ff;
  border: 2px solid #241b5a;
  padding: 0.4rem 0.95rem;
  box-shadow: 0 1px 2px rgba(36, 27, 90, 0.12);
}

.theme-change-btn:hover,
.theme-change-btn:focus {
  color: #fff;
  background: #241b5a;
  border-color: #241b5a;
}

.theme-picker-label {
  font-size: 15px;
  font-weight: 700;
  color: #241b5a;
}

.theme-picker-meta {
  margin-top: 4px;
  font-size: 13px;
  color: #64748b;
}

.theme-code {
  margin-left: 6px;
  color: #94a3b8;
  font-size: 12px;
}

.theme-search {
  max-width: 260px;
}

.theme-filter-row {
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.theme-filter {
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #475569;
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.theme-filter-active {
  background: #241b5a;
  border-color: #241b5a;
  color: #fff;
}

.theme-status {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  vertical-align: middle;
}

.theme-status-live {
  background: #dcfce7;
  color: #166534;
}

.theme-status-draft {
  background: #fef3c7;
  color: #92400e;
}

.theme-grid-wrap {
  max-height: min(58vh, 620px);
  overflow-y: auto;
  padding: 8px 8px 4px;
  margin: 0 -4px 4px;
}

.theme-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 22px 16px;
}

.theme-card {
  background: transparent;
  border: 0;
  box-shadow: none;
  overflow: visible;
  text-align: center;
}

.theme-card:hover {
  transform: none;
}

.theme-phone {
  position: relative;
  width: 100%;
  aspect-ratio: 9 / 18.5;
  border-radius: 22px;
  overflow: hidden;
  background: #111827;
  border: 3px solid #e5e7eb;
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.12);
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.theme-card:hover .theme-phone {
  transform: translateY(-3px);
  box-shadow: 0 18px 36px rgba(36, 27, 90, 0.16);
}

.theme-card-selected .theme-phone {
  border-color: #241b5a;
  box-shadow: 0 0 0 3px rgba(36, 27, 90, 0.28), 0 14px 30px rgba(36, 27, 90, 0.18);
}

.theme-card-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
  display: block;
  background: #f8fafc;
}

.theme-card-fallback {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 700;
  color: #241b5a;
  background: linear-gradient(135deg, #eef2ff 0%, #f5f3ff 100%);
}

.theme-phone-overlay {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 12px;
  background: linear-gradient(180deg, rgba(15, 23, 42, 0) 40%, rgba(15, 23, 42, 0.72) 100%);
  opacity: 0;
  transition: opacity 0.18s ease;
}

.theme-card:hover .theme-phone-overlay,
.theme-card-selected .theme-phone-overlay {
  opacity: 1;
}

.theme-overlay-btn {
  border: 0;
  border-radius: 8px;
  padding: 7px 10px;
  font-size: 12px;
  font-weight: 700;
  background: #fff;
  color: #0f172a;
  cursor: pointer;
}

.theme-overlay-btn-primary {
  background: #241b5a;
  color: #fff;
}

.theme-card-badge {
  position: absolute;
  top: 10px;
  left: 10px;
  background: #166534;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 999px;
  z-index: 2;
}

.theme-card-badge-selected {
  background: #241b5a;
}

.theme-card-name {
  margin-top: 10px;
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.3;
}

.theme-publish-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 12px;
  padding-top: 14px;
  border-top: 1px solid #e2e8f0;
  flex-wrap: wrap;
  position: sticky;
  bottom: 0;
  background: #f8fafc;
  z-index: 3;
}

.theme-publish-copy {
  font-size: 13px;
  color: #475569;
}

.theme-empty {
  padding: 24px 8px;
  color: #64748b;
  font-size: 14px;
}

.theme-skel-card {
  cursor: default;
  pointer-events: none;
}

.theme-skel-label {
  height: 14px;
  width: 60%;
  margin: 12px;
  border-radius: 4px;
  background: #e8eaef;
}

.skel-shimmer {
  background: linear-gradient(90deg, #e8eaef 25%, #f3f4f7 50%, #e8eaef 75%);
  background-size: 200% 100%;
  animation: theme-skel-shimmer 1.4s ease infinite;
}

@keyframes theme-skel-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.theme-lightbox {
  position: fixed;
  inset: 0;
  z-index: 20000;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.theme-lightbox-card {
  width: min(860px, 100%);
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.25);
}

.theme-lightbox-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}

.theme-lightbox-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.theme-lightbox-code {
  font-size: 12px;
  color: #94a3b8;
}

.theme-lightbox-close {
  border: 0;
  background: #f1f5f9;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
}

.theme-lightbox-image {
  width: 100%;
  max-height: 70vh;
  object-fit: contain;
  background: #f8fafc;
  border-radius: 8px;
}

.theme-lightbox-note {
  margin: 12px 0 0;
  font-size: 12px;
  color: #64748b;
}

@media (max-width: 767px) {
  .theme-picker {
    padding: 12px;
  }

  .theme-search {
    max-width: none;
    width: 100%;
  }

  .theme-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .theme-grid-wrap {
    max-height: min(70vh, 720px);
  }

  .theme-publish-bar .btn {
    width: 100%;
  }

  .theme-selected-actions {
    width: 100%;
  }

  .theme-selected-actions .btn {
    flex: 1 1 auto;
  }

  .theme-modal-footer-copy {
    width: 100%;
    margin-right: 0;
    padding-right: 0;
  }
}
</style>
